import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {getPriceData} from '../../../../app/utils/product-utils'
import {SCHEMA_PDP} from '../../../constants'

const SCRIPT_ID = 'pdp-structured-data'

const getPageDescription = (pageMetaTags) => {
    let description

    if (pageMetaTags) {
        for (let i = 0; i < pageMetaTags.length && !description; i++) {
            let tag = pageMetaTags[i]
            if (tag && tag.id === 'description') {
                description = tag.value || undefined
            }
        }
    }

    return description
}

const buildCategory = (product) => {
    const breadcrumbCategory = product?.c_breadcrumbs
        ?.map((crumb) => crumb?.htmlValue)
        ?.filter(Boolean)
        ?.join(' > ')

    return breadcrumbCategory || product?.c_IconSuite?.[0] || undefined
}

const getProductImage = (product, selectedVariant) => {
    const variantImage = selectedVariant?.image?.disBaseLink || selectedVariant?.image?.link
    const productImage =
        product?.image?.disBaseLink ||
        product?.image?.link ||
        product?.c_images?.largeImage ||
        product?.c_images?.mediumImage
    const image = variantImage || productImage
    return image ? [image] : undefined
}

const getSimilarProductName = (product, link) => {
    const matchedVariant = product?.variants?.find((variant) => variant?.productId === link?.id)
    return matchedVariant?.name || matchedVariant?.productName || product?.name
}

const getSimilarProducts = (product, selectedVariant) => {
    const currentProductIds = new Set(
        [product?.id, product?.productId, selectedVariant?.productId].filter(Boolean)
    )
    const currentSlugUrl = product?.slugUrl
    const byId = new Set()

    const related = product?.c_allPdpLinks
        ?.filter(
            (link) => link?.id && !currentProductIds.has(link.id) && link?.url !== currentSlugUrl
        )
        ?.map((link) => ({
            '@type': SCHEMA_PDP.type,
            '@id': link?.url,
            sku: link?.id,
            name: getSimilarProductName(product, link)
        }))
        ?.filter((item) => item?.['@id'] && !byId.has(item['@id']) && byId.add(item['@id']))

    return related?.length ? related : undefined
}

const getOfferLevelReturnPolicy = (product) => {
    const isFinalSaleOverride =
        !product?.c_isReturnable ||
        product?.c_isEngravable ||
        Boolean(product?.c_giftCard) ||
        Boolean(product?.c_isCustomizeBadgeActive && product?.c_colorBarAsset)

    if (!isFinalSaleOverride) {
        return undefined
    }

    return {
        '@type': SCHEMA_PDP.returnType,
        returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
    }
}

const getOffers = (product) => {
    let offers = {}
    let price = getPriceData(product, 1)

    if (price && product) {
        offers.url = product.slugUrl
        offers['@type'] = SCHEMA_PDP.offerType
        offers.priceCurrency = product.c_selectedCurrency
        if (price.currentPrice) {
            offers.price = parseFloat(price.currentPrice).toFixed(2).toString()
        }
        if (product?.c_offerUntilDate) {
            offers.priceValidUntil = product.c_offerUntilDate
        }
        offers.availability = SCHEMA_PDP.availability
        offers.itemCondition = SCHEMA_PDP.itemCondition
        offers.seller = {
            '@type': SCHEMA_PDP.organizationType,
            name: product?.c_Brand || SCHEMA_PDP.brandName
        }
        const offerLevelReturnPolicy = getOfferLevelReturnPolicy(product)
        if (offerLevelReturnPolicy) {
            offers.hasMerchantReturnPolicy = offerLevelReturnPolicy
        }
    }

    return offers
}

const getVariationDisplayValue = (product, selectedVariant, attributeIds = []) => {
    const selectedVariationValues =
        selectedVariant?.variationValues || product?.variationValues || {}

    for (const attributeId of attributeIds) {
        const rawValue = selectedVariationValues?.[attributeId]
        if (!rawValue) continue

        const attribute = product?.variationAttributes?.find((attr) => attr?.id === attributeId)
        const matchedValue = attribute?.values?.find((value) => value?.value === rawValue)

        if (matchedValue?.name) {
            return matchedValue.name
        }

        if (rawValue) {
            return rawValue
        }
    }

    return undefined
}

const getAdditionalProperties = (product, selectedVariant) => {
    const materialVariationName = getVariationDisplayValue(product, selectedVariant, ['metal'])
    const stoneVariationName = getVariationDisplayValue(product, selectedVariant, [
        'stoneColor',
        'color'
    ])

    const materialValue = materialVariationName || product?.c_metal || product?.c_material
    const stoneValue = stoneVariationName || product?.c_color || product?.c_stone

    const additionalProperty = [
        materialValue && {
            '@type': 'PropertyValue',
            name: 'Material',
            value: materialValue
        },
        stoneValue && {
            '@type': 'PropertyValue',
            name: 'Stone',
            value: stoneValue
        }
    ].filter(Boolean)

    return additionalProperty.length ? additionalProperty : undefined
}

const getBazaarvoiceReviews = () =>
    SCHEMA_PDP.bazaarvoiceIds.reduce((reviews, id) => {
        const script = document.getElementById(id)
        if (!script) return reviews

        script.remove()

        try {
            const data = JSON.parse(script.textContent)

            return {
                ...reviews,
                ...('aggregateRating' in data && {
                    aggregateRating:
                        data.aggregateRating?.reviewCount > 0 ? data.aggregateRating : undefined
                }),
                ...('review' in data && {
                    review: data.review?.length
                        ? data.review.map((item) => ({...item, '@type': SCHEMA_PDP.reviewType}))
                        : undefined
                })
            }
        } catch (e) {
            return reviews
        }
    }, {})

const Schema = ({product, selectedVariant}) => {
    const [reviews, setReviews] = useState({})
    let schema = {}

    useEffect(() => {
        setReviews({})

        const updateReviews = () => {
            const bazaarvoiceReviews = getBazaarvoiceReviews()
            if (Object.keys(bazaarvoiceReviews).length) {
                setReviews((prev) => ({...prev, ...bazaarvoiceReviews}))
            }
        }

        updateReviews()
        const observer = new MutationObserver(updateReviews)
        observer.observe(document.head, {childList: true})

        return () => observer.disconnect()
    }, [product?.id])

    if (product) {
        const brandName = product?.c_Brand || product?.brand || SCHEMA_PDP.brandName
        schema = {
            '@context': SCHEMA_PDP.context,
            '@type': SCHEMA_PDP.type,
            '@id': product.slugUrl ? `${product.slugUrl}#product` : undefined,
            url: product.slugUrl,
            name: product.name,
            description: getPageDescription(product.pageMetaTags),
            sku: product.id,
            mpn: product.id,
            material: product.c_material || undefined,
            color: product.c_color,
            size: product.c_size?.toLowerCase() === 'os' ? 'One Size' : product.c_size,
            category: buildCategory(product),
            image: getProductImage(product, selectedVariant),
            brand: {
                '@type': 'Brand',
                name: brandName
            },
            additionalProperty: getAdditionalProperties(product, selectedVariant),
            offers: getOffers(product),
            isSimilarTo: getSimilarProducts(product, selectedVariant),
            hasMerchantReturnPolicy: {
                '@type': SCHEMA_PDP.returnType,
                url: SCHEMA_PDP.returnURL,
                returnPolicyCategory: SCHEMA_PDP.returnCategory,
                merchantReturnDays: 30,
                additionalType: SCHEMA_PDP.additionalType
            },
            ...reviews
        }

        if (product?.c_weight) {
            schema.weight = product.c_weight
        }
    }

    return (
        <script
            id={SCRIPT_ID}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
                __html: JSON.stringify(schema).split('<').join('\\u003c')
            }}
        />
    )
}

Schema.propTypes = {
    product: PropTypes.shape({
        id: PropTypes.string,
        imageGroups: PropTypes.array,
        variationAttributes: PropTypes.array,
        name: PropTypes.string,
        slugUrl: PropTypes.string,
        productId: PropTypes.string,
        c_color: PropTypes.string,
        variants: PropTypes.array,
        pageMetaTags: PropTypes.array,
        c_material: PropTypes.string,
        c_metal: PropTypes.string,
        c_stone: PropTypes.string,
        c_size: PropTypes.string,
        variationValues: PropTypes.object,
        c_IconSuite: PropTypes.array,
        c_breadcrumbs: PropTypes.array,
        c_allPdpLinks: PropTypes.array,
        c_Brand: PropTypes.string,
        brand: PropTypes.string,
        c_weight: PropTypes.number,
        c_isReturnable: PropTypes.bool,
        c_isEngravable: PropTypes.bool,
        c_giftCard: PropTypes.bool,
        c_isCustomizeBadgeActive: PropTypes.bool,
        c_colorBarAsset: PropTypes.string
    }),
    selectedVariant: PropTypes.shape({
        image: PropTypes.shape({
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        imageGroups: PropTypes.array,
        variationValues: PropTypes.object
    })
}

export default Schema
